import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2, XCircle, AlertTriangle, Info, X, ArrowLeft, Loader2,
  Clock, Wallet, CalendarRange, FileText, Lock, Mail, Check, Plus,
  ChevronDown,
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import UsageMeter from '../../components/billing/UsageMeter';
import PaymentMethodCard from '../../components/billing/PaymentMethodCard';
import InvoiceList from '../../components/billing/InvoiceList';
import { clientService } from '../../services';

// Shared chrome — matches admin Analytics / client SessionsPage.
const CARD_BASE =
  'rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.08)]';

const SECTION_HEADER_CHIP =
  'w-9 h-9 rounded-xl flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 text-neutral-600 dark:text-neutral-400 flex-shrink-0';

const PLAN_HIERARCHY = { free: 0, pro: 1, pro_plus: 2, enterprise_starter: 3, enterprise_scale: 4 };

const PLAN_NAMES = {
  free: 'Free Trial',
  pro: 'Pro',
  pro_plus: 'Pro Plus',
  enterprise_starter: 'Enterprise Starter',
  enterprise_scale: 'Enterprise Scale',
};

/**
 * Subscription Management Page
 * Restructured with main tabs and sub-tabs
 * Main Tabs: Subscription Details & Billing | All Plans
 * Sub Tabs (under Subscription Details): Overview | Payment Methods | Invoices
 */

const SubscriptionPage = () => {
  const navigate = useNavigate();

  // Main tab state
  const [mainTab, setMainTab] = useState('subscription'); // 'subscription' | 'plans'

  // Sub tab state (for Subscription Details & Billing)
  const [subTab, setSubTab] = useState('overview'); // 'overview' | 'payment-methods' | 'invoices'

  // Billing cycle for plans
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'annual'

  // Data states
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sync state for pending plans
  const [pendingPlan, setPendingPlan] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);

  // Modal states
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Upgrade/downgrade modal states
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [selectedPlanForAction, setSelectedPlanForAction] = useState(null);
  const [downgradeLoading, setDowngradeLoading] = useState(false);
  const [scheduledDowngrade, setScheduledDowngrade] = useState(null);

  // Add-on states
  const [addonLoading, setAddonLoading] = useState(false);

  // Toast notification state
  const [toast, setToast] = useState(null); // { type: 'success'|'error'|'warning'|'info', title, message }

  const showToast = useCallback((type, title, message) => {
    setToast({ type, title, message });
    setTimeout(() => setToast(null), 5000);
  }, []);

  // Handle trainer add-on purchase
  const handleTrainerAddon = async () => {
    try {
      setAddonLoading(true);
      const response = await clientService.purchaseTrainerAddon();
      if (response.success && response.data?.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      }
    } catch (err) {
      showToast('error', 'Add-on Error', err.message || 'Failed to initiate trainer add-on purchase');
      setAddonLoading(false);
    }
  };

  // Plans data with monthly and annual pricing (Updated as per PDF)
  const plans = [
    {
      id: 'free',
      name: 'Free',
      monthlyPrice: 0,
      monthlyPriceUSD: 0,
      description: 'Try Aktivity free for 1 month. Upgrade to continue beyond that.',
      features: [
        '3 sessions total (one-time)',
        'Up to 30 participants/session',
        'All core features',
        '1 month only — no renewal',
      ],
      buttonLabel: 'Start Free',
    },
    {
      id: 'pro',
      name: 'Pro',
      monthlyPrice: 1999,
      monthlyPriceUSD: 24,
      description: 'For trainers who want proof of impact',
      features: [
        '20 sessions/month',
        '30 participants/session',
        '1 trainer access',
      ],
      buttonLabel: 'Choose Pro',
    },
    {
      id: 'pro_plus',
      name: 'Pro Plus',
      monthlyPrice: 5999,
      monthlyPriceUSD: 72,
      description: 'Learning intelligence at scale',
      features: [
        'Unlimited sessions',
        '100 participants/session',
        'Up to 3 trainer accounts',
        'Priority support',
      ],
      buttonLabel: 'Go Pro Plus',
    },
  ];

  // Enterprise plans data
  const enterprisePlans = [
    {
      id: 'enterprise_starter',
      name: 'Enterprise Starter',
      price: 180000, // ₹1,80,000/year
      priceUSD: 2160, // $2,160/year
      description: 'For L&D Teams & Training Companies',
      features: [
        '200+ participants/session',
        'Up to 5 trainer accounts',
        'Unlimited sessions',
        'Live dashboards',
        'Trainer leaderboards',
        'Program-level learning intelligence',
      ],
      buttonLabel: 'Choose Enterprise Starter',
    },
    {
      id: 'enterprise_scale',
      name: 'Enterprise Scale',
      price: 0, // Custom pricing
      priceUSD: 0,
      isCustom: true,
      description: 'Talk to Sales',
      features: [],
      addOns: [
        { label: 'Additional Trainer', price: '₹1,000/mo | $20/mo' },
        { label: 'Extra participants', price: 'Custom pricing' },
        { label: 'White-label & advanced analytics', price: '' },
      ],
      buttonLabel: 'Talk to Sales',
    },
  ];

  // Get price for monthly plans (Pro/Pro Plus are monthly only)
  const getPlanPrice = (plan) => {
    if (plan.monthlyPrice === 0) return { price: 0, priceUSD: 0, interval: 'forever', savings: 0, savingsUSD: 0 };
    return { price: plan.monthlyPrice, priceUSD: plan.monthlyPriceUSD, interval: 'month', savings: 0, savingsUSD: 0 };
  };

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load subscription and billing data in parallel
      const [subResponse, billingResponse] = await Promise.all([
        clientService.getSubscription(),
        clientService.getBillingOverview(),
      ]);

      // Process subscription data
      if (subResponse.success) {
        let sub = subResponse.data;
        const apiUsage = sub.usage;

        // Check if there's a pending plan that needs to be synced
        // This happens when payment was made but webhook hasn't processed yet
        if (sub.pendingPlan && (sub.plan === 'free' || sub.subscriptionStatus === 'pending')) {
          console.log('Pending plan detected, syncing subscription...');
          setPendingPlan(sub.pendingPlan);
          try {
            const billingCycle = ['enterprise_starter', 'enterprise_scale'].includes(sub.pendingPlan) ? 'yearly' : 'monthly';
            const syncResponse = await clientService.syncSubscription(sub.pendingPlan, billingCycle);
            if (syncResponse.success) {
              console.log('Subscription synced successfully');
              setPendingPlan(null);
              // Reload subscription data after sync
              const newSubResponse = await clientService.getSubscription();
              if (newSubResponse.success) {
                sub = newSubResponse.data;
              }
            }
          } catch (syncError) {
            console.error('Failed to sync subscription:', syncError);
            // Keep pendingPlan set so user can manually retry
          }
        } else {
          setPendingPlan(null);
        }

        // Set scheduled downgrade if present
        if (sub.scheduledDowngrade?.plan) {
          setScheduledDowngrade(sub.scheduledDowngrade);
        } else {
          setScheduledDowngrade(null);
        }

        const periodStart = sub.currentPeriodStart ? new Date(sub.currentPeriodStart).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
        const periodEnd = sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd).toISOString().split('T')[0] : '';

        setSubscription({
          id: sub._id,
          plan: sub.plan ? sub.plan.charAt(0).toUpperCase() + sub.plan.slice(1) : 'Free',
          planId: sub.plan || 'free',
          price: sub.price || 0,
          gstRate: sub.gstRate || 18,
          gstAmount: sub.gstAmount || 0,
          totalWithGst: sub.totalWithGst || sub.price || 0,
          interval: sub.interval || 'month',
          status: sub.status || 'active',
          // Carried through from the API deliberately. This state object is an explicit
          // whitelist, and `expired` was missing from it — so `subscription.expired` was
          // permanently undefined and the entire "Previous Plan (Expired)" card, Renew
          // button included, could never render. Users saw the normal Current Plan card
          // with an "expired" badge instead, which offers no way to buy the plan back.
          // isPlanExpired() on the server is date-based, so it can be true while
          // `status` still says active — read the flag, not the status.
          expired: Boolean(sub.expired),
          currentPeriodStart: periodStart,
          currentPeriodEnd: periodEnd,
          cancelAtPeriodEnd: sub.cancelAtPeriodEnd || false,
          autoRenew: !sub.cancelAtPeriodEnd,
          features: {
            sessions: sub.features?.sessions || 3,
            participants: sub.features?.participants || 30,
            trainers: sub.features?.trainers || 1,
            storage: sub.features?.storage || 100,
            aiAnalytics: sub.features?.aiAnalytics || false,
            support: sub.features?.support || 'email',
          },
        });

        if (apiUsage) {
          setUsage({
            sessions: { used: apiUsage.sessions?.used || 0, limit: apiUsage.sessions?.limit || sub.features?.sessions || 3 },
            participants: { used: apiUsage.participants?.used || 0, limit: apiUsage.participants?.limit || sub.features?.participants || 30 },
            trainers: { used: apiUsage.trainers?.used || 0, limit: apiUsage.trainers?.limit || sub.features?.trainers || 1 },
            storage: { used: apiUsage.storage?.used || 0, limit: apiUsage.storage?.limit || sub.features?.storage || 100 },
          });
        } else {
          setUsage({
            sessions: { used: 0, limit: sub.features?.sessions || 3 },
            participants: { used: 0, limit: sub.features?.participants || 30 },
            trainers: { used: 0, limit: sub.features?.trainers || 1 },
            storage: { used: 0, limit: sub.features?.storage || 100 },
          });
        }
      }

      // Process billing data
      if (billingResponse.success) {
        const { paymentMethods: pms, recentInvoices } = billingResponse.data;

        if (pms && pms.length > 0) {
          const transformedPms = pms.map((pm) => ({
            id: pm._id,
            type: pm.type || 'card',
            brand: pm.card?.brand || 'Card',
            last4: pm.card?.last4 || '****',
            expMonth: pm.card?.expiryMonth || '',
            expYear: pm.card?.expiryYear || '',
            isDefault: pm.isDefault || false,
          }));
          setPaymentMethods(transformedPms);
        }

        if (recentInvoices && recentInvoices.length > 0) {
          const transformedInvoices = recentInvoices.map((inv) => ({
            id: inv._id,
            date: inv.createdAt?.split('T')[0] || '',
            description: inv.description || `${inv.subscription?.plan || 'Plan'} - ${inv.billingPeriod || 'Monthly'}`,
            amount: inv.total || 0,
            status: inv.status || 'pending',
            invoiceNumber: inv.invoiceNumber || '',
            pdfUrl: inv.pdfUrl || '#',
          }));
          setInvoices(transformedInvoices);
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Manual sync for pending subscriptions
  const handleManualSync = async () => {
    if (!pendingPlan) return;

    try {
      setSyncLoading(true);
      const billingCycle = ['enterprise_starter', 'enterprise_scale'].includes(pendingPlan) ? 'yearly' : 'monthly';
      const syncResponse = await clientService.syncSubscription(pendingPlan, billingCycle);

      if (syncResponse.success) {
        setPendingPlan(null);
        // Reload all data
        await loadAllData();
      }
    } catch (error) {
      console.error('Failed to sync subscription:', error);
      showToast('error', 'Sync Failed', 'Failed to sync subscription. Please try again or contact support.');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      const response = await clientService.cancelSubscription(cancelReason);
      if (response.success) {
        setSubscription((prev) => ({
          ...prev,
          cancelAtPeriodEnd: true,
          autoRenew: false,
        }));
        setShowCancelModal(false);
        showToast('success', 'Subscription Cancelled', 'Your subscription will be cancelled at the end of the current billing period.');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      showToast('error', 'Error', error.response?.data?.message || 'Failed to cancel subscription.');
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      const response = await clientService.reactivateSubscription();
      if (response.success) {
        // Reload all data to get the restored plan from the server
        await loadAllData();
        showToast('success', 'Subscription Reactivated', 'Your subscription has been reactivated successfully.');
      }
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      showToast('error', 'Error', error.response?.data?.message || 'Failed to reactivate subscription.');
    }
  };

  const handleSetDefaultPayment = async (paymentMethodId) => {
    try {
      const response = await clientService.setDefaultPaymentMethod(paymentMethodId);
      if (response.success) {
        setPaymentMethods((prev) =>
          prev.map((pm) => ({
            ...pm,
            isDefault: pm.id === paymentMethodId,
          }))
        );
      }
    } catch (error) {
      console.error('Error setting default payment:', error);
      showToast('error', 'Error', error.response?.data?.message || 'Failed to set default payment method.');
    }
  };

  const handleDeletePayment = async (paymentMethodId) => {
    if (window.confirm('Are you sure you want to delete this payment method?')) {
      try {
        const response = await clientService.deletePaymentMethod(paymentMethodId);
        if (response.success) {
          setPaymentMethods((prev) => prev.filter((pm) => pm.id !== paymentMethodId));
        }
      } catch (error) {
        console.error('Error deleting payment method:', error);
        showToast('error', 'Error', error.response?.data?.message || 'Failed to delete payment method.');
      }
    }
  };

  const handleSelectPlan = async (planId) => {
    // Enterprise Scale - Custom pricing, contact sales
    if (planId === 'enterprise_scale') {
      window.location.href = 'mailto:sales@aktivity.io?subject=Enterprise Scale Inquiry&body=Hi, I am interested in the Enterprise Scale plan. Please contact me to discuss pricing and features.';
      return;
    }

    const currentPlan = subscription?.planId || 'free';

    // Free plan is one-time only. Anyone currently on a paid plan has already
    // exhausted their free trial (or skipped it by signing up paid) — either
    // way, they can't downgrade back to Free. Block here with a clear toast
    // before opening the downgrade modal.
    if (planId === 'free' && currentPlan !== 'free') {
      showToast(
        'error',
        'Free plan unavailable',
        "You've already used your Free trial. Free plan can only be used once. Pick Pro or Pro Plus to continue."
      );
      return;
    }

    /**
     * While the plan is expired every purchase is a straight re-buy — there is no
     * access to lose and no proration to protect, so the upgrade/downgrade distinction
     * does not apply. Send it directly to checkout.
     *
     * Without this, picking the tier you already had hit the `selectedLevel ===
     * currentLevel` no-op below and nothing happened at all, and picking a cheaper one
     * opened the schedule-a-downgrade modal — which is meaningless for a plan that has
     * already ended. Mirrors the server, which now skips its own downgrade guard when
     * isPlanExpired() is true.
     */
    if (subscription?.expired) {
      startCheckout(planId);
      return;
    }

    const currentLevel = PLAN_HIERARCHY[currentPlan] ?? 0;
    const selectedLevel = PLAN_HIERARCHY[planId] ?? 0;

    if (selectedLevel > currentLevel) {
      // Upgrade - show confirmation then redirect to checkout
      setSelectedPlanForAction(planId);
      setShowUpgradeModal(true);
    } else if (selectedLevel < currentLevel) {
      // Downgrade — including "Start Free" from a paid plan. Falls through
      // to scheduleDowngrade so the change applies at the end of the billing
      // period, instead of dumping the user on /client/pricing where nothing
      // actually happens.
      setSelectedPlanForAction(planId);
      setShowDowngradeModal(true);
    }
    // selectedLevel === currentLevel → no-op (button shows "Current Plan").
  };

  /**
   * Create a Dodo checkout session and hand off to their payment page.
   *
   * Shared by the upgrade modal and the Renew button on an expired plan — renewing is
   * the same purchase, it just skips plan selection because the plan is already known.
   */
  const startCheckout = async (planId) => {
    if (!planId) return;

    try {
      setCheckoutLoading(true);
      // Enterprise plans are yearly, all others are monthly only
      const cycle = planId.startsWith('enterprise') ? 'yearly' : 'monthly';

      localStorage.setItem('pending_plan_id', planId);
      localStorage.setItem('pending_billing_cycle', cycle);

      const response = await clientService.createCheckoutSession(planId, cycle);

      if (response.success && response.data?.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else {
        showToast('error', 'Checkout Failed', 'Failed to create checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      showToast('error', 'Checkout Failed', error.response?.data?.error || 'Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleConfirmUpgrade = async () => {
    setShowUpgradeModal(false);
    await startCheckout(selectedPlanForAction);
  };

  /**
   * Renew the plan that just expired — straight to payment, no plan picker.
   *
   * subscriptionCron leaves organization.plan on the last paid tier when a plan lapses,
   * so `subscription.plan` still names what to buy back. The backend skips its
   * downgrade guard while expired, which is what makes buying the same tier possible
   * at all — before that, renewing Pro was rejected as a downgrade from Pro.
   */
  const handleRenewExpiredPlan = async () => {
    // planId, NOT plan. This state object stores `plan` capitalised for display
    // ("Pro") and keeps the raw identifier in `planId` ("pro"). The checkout endpoint
    // validates against the lowercase enum, so passing the display string would come
    // back as "Invalid plan selected".
    await startCheckout(subscription?.planId);
  };

  const handleConfirmDowngrade = async () => {
    const planId = selectedPlanForAction;

    try {
      setDowngradeLoading(true);
      const response = await clientService.scheduleDowngrade(planId);

      if (response.success) {
        setShowDowngradeModal(false);

        // Backend returns one of two shapes:
        //   - { applied: true, plan, currentPeriodEnd }       → already-expired path; downgrade was applied immediately
        //   - { scheduledDowngrade: { plan, scheduledDate } } → active path; cron will apply at period end
        if (response.data?.applied) {
          showToast(
            'success',
            'Plan Switched',
            `You're now on the ${PLAN_NAMES[planId] || planId.replace('_', ' ')} plan.`
          );
          // Reload so the Current Plan card flips from "Previous Plan (Expired)"
          // back to a normal active card with the new plan's features + dates.
          await loadAllData();
        } else if (response.data?.scheduledDowngrade) {
          setScheduledDowngrade(response.data.scheduledDowngrade);
          showToast(
            'success',
            'Downgrade Scheduled',
            `Your plan will be downgraded to ${PLAN_NAMES[planId]} on ${formatDate(response.data.scheduledDowngrade.scheduledDate)}.`
          );
        }
      }
    } catch (error) {
      console.error('Downgrade error:', error);
      showToast('error', 'Error', error.response?.data?.error || 'Failed to schedule downgrade. Please try again.');
    } finally {
      setDowngradeLoading(false);
    }
  };

  const handleCancelScheduledDowngrade = async () => {
    try {
      const response = await clientService.cancelScheduledDowngrade();
      if (response.success) {
        setScheduledDowngrade(null);
        showToast('success', 'Downgrade Cancelled', 'Scheduled downgrade has been cancelled.');
      }
    } catch (error) {
      console.error('Error cancelling downgrade:', error);
      showToast('error', 'Error', error.response?.data?.error || 'Failed to cancel downgrade.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 size={32} strokeWidth={1.75} className="animate-spin mx-auto text-primary-600 mb-3" />
            <p className="text-sm text-neutral-600 dark:text-neutral-400">Loading subscription…</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="text-center py-12 px-4">
          <span className="inline-flex w-16 h-16 rounded-2xl items-center justify-center bg-rose-50 text-rose-500 ring-1 ring-inset ring-rose-100 dark:bg-rose-500/10 dark:text-rose-400 dark:ring-rose-500/20 mb-4">
            <AlertTriangle size={28} strokeWidth={1.75} />
          </span>
          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-1">Something went wrong</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">{error}</p>
          <Button variant="primary" onClick={loadAllData}>Try Again</Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Toast Notification */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -20, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: -20, x: '-50%' }}
            className="fixed top-6 left-1/2 z-50 w-full max-w-md px-4"
          >
            <div className={`flex items-start gap-3 p-4 rounded-xl shadow-lg ring-1 ring-inset ${
              toast.type === 'success' ? 'bg-emerald-50 ring-emerald-200/60 dark:bg-emerald-500/10 dark:ring-emerald-500/30' :
              toast.type === 'error' ? 'bg-rose-50 ring-rose-200/60 dark:bg-rose-500/10 dark:ring-rose-500/30' :
              toast.type === 'warning' ? 'bg-amber-50 ring-amber-200/60 dark:bg-amber-500/10 dark:ring-amber-500/30' :
              'bg-blue-50 ring-blue-200/60 dark:bg-blue-500/10 dark:ring-blue-500/30'
            }`}>
              <div className="flex-shrink-0 mt-0.5">
                {toast.type === 'success' && <CheckCircle2 size={20} strokeWidth={1.75} className="text-emerald-600 dark:text-emerald-400" />}
                {toast.type === 'error' && <XCircle size={20} strokeWidth={1.75} className="text-rose-600 dark:text-rose-400" />}
                {toast.type === 'warning' && <AlertTriangle size={20} strokeWidth={1.75} className="text-amber-600 dark:text-amber-400" />}
                {toast.type === 'info' && <Info size={20} strokeWidth={1.75} className="text-blue-600 dark:text-blue-400" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${
                  toast.type === 'success' ? 'text-emerald-900 dark:text-emerald-200' :
                  toast.type === 'error' ? 'text-rose-900 dark:text-rose-200' :
                  toast.type === 'warning' ? 'text-amber-900 dark:text-amber-200' :
                  'text-blue-900 dark:text-blue-200'
                }`}>{toast.title}</p>
                <p className={`text-sm mt-0.5 ${
                  toast.type === 'success' ? 'text-emerald-800/90 dark:text-emerald-300/90' :
                  toast.type === 'error' ? 'text-rose-800/90 dark:text-rose-300/90' :
                  toast.type === 'warning' ? 'text-amber-800/90 dark:text-amber-300/90' :
                  'text-blue-800/90 dark:text-blue-300/90'
                }`}>{toast.message}</p>
              </div>
              <button type="button" onClick={() => setToast(null)} className="flex-shrink-0 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors">
                <X size={16} strokeWidth={1.75} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="w-full px-4 lg:px-6 py-6 max-w-6xl mx-auto space-y-5 pb-20 sm:pb-6">
        {/* Header */}
        <div className="space-y-1">
          <button
            type="button"
            onClick={() => navigate('/client/dashboard')}
            className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100 transition-colors mb-1 group"
          >
            <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-0.5" />
            <span className="font-medium">Back to Dashboard</span>
          </button>
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">Subscription &amp; Billing</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">Manage your subscription, billing, and view available plans</p>
        </div>

        {/* Pending Plan Sync Banner */}
        {pendingPlan && (
          <div className="rounded-xl bg-amber-50 dark:bg-amber-500/10 ring-1 ring-inset ring-amber-200/60 dark:ring-amber-500/20 p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start gap-3">
                <Clock size={20} strokeWidth={1.75} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">Subscription Update Pending</p>
                  <p className="text-sm text-amber-800/90 dark:text-amber-300/90 mt-1">
                    Your payment for the {pendingPlan.replace('_', ' ').toUpperCase()} plan was received. Click sync to activate your subscription.
                  </p>
                </div>
              </div>
              <Button variant="primary" size="sm" onClick={handleManualSync} disabled={syncLoading} className="self-start sm:self-auto flex-shrink-0">
                {syncLoading ? (
                  <>
                    <Loader2 size={14} strokeWidth={1.75} className="animate-spin mr-1.5" />
                    Syncing…
                  </>
                ) : 'Sync Now'}
              </Button>
            </div>
          </div>
        )}

        {/* Main Tabs — pill chip toggle */}
        <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 flex-wrap w-full sm:w-auto">
          {[
            { value: 'subscription', label: 'Subscription Details & Billing' },
            { value: 'plans',        label: 'All Plans' },
          ].map((o) => {
            const active = mainTab === o.value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => setMainTab(o.value)}
                className={`flex-1 sm:flex-none px-3 sm:px-4 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                  active
                    ? 'bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-200 dark:bg-primary-500/10 dark:text-primary-300 dark:ring-primary-500/30 shadow-sm'
                    : 'text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100'
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>

        {/* Subscription Details & Billing Tab */}
        {mainTab === 'subscription' && (
          <div className="space-y-5">
            {/* Sub Tabs */}
            <div className="border-b border-neutral-200 dark:border-neutral-800/70">
              <div className="flex gap-4 sm:gap-6 overflow-x-auto">
                {[
                  { id: 'overview', label: 'Overview' },
                  { id: 'payment-methods', label: 'Payment Methods' },
                  { id: 'invoices', label: 'Invoices' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setSubTab(tab.id)}
                    className={`pb-3 text-sm font-medium transition-colors relative whitespace-nowrap ${
                      subTab === tab.id
                        ? 'text-primary-600'
                        : 'text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200'
                    }`}
                  >
                    {tab.label}
                    {subTab === tab.id && (
                      <motion.div
                        layoutId="subTabIndicator"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500 rounded-t"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Overview Sub Tab */}
            {subTab === 'overview' && (
              <div className="space-y-4">
                {/* Cancellation Warning */}
                {subscription?.cancelAtPeriodEnd && (
                  <div className="rounded-xl bg-amber-50 dark:bg-amber-500/10 ring-1 ring-inset ring-amber-200/60 dark:ring-amber-500/20 p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle size={20} strokeWidth={1.75} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">Subscription Cancelled</p>
                        <p className="text-sm text-amber-800/90 dark:text-amber-300/90 mt-1">
                          Your subscription will be cancelled on {formatDate(subscription.currentPeriodEnd)}.
                        </p>
                        <Button variant="primary" size="sm" onClick={handleReactivateSubscription} className="mt-3">
                          Reactivate Subscription
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Scheduled Downgrade Warning */}
                {scheduledDowngrade?.plan && (
                  <div className="rounded-xl bg-blue-50 dark:bg-blue-500/10 ring-1 ring-inset ring-blue-200/60 dark:ring-blue-500/20 p-4">
                    <div className="flex items-start gap-3">
                      <Info size={20} strokeWidth={1.75} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">Downgrade Scheduled</p>
                        <p className="text-sm text-blue-800/90 dark:text-blue-300/90 mt-1">
                          Your plan will change to <strong>{PLAN_NAMES[scheduledDowngrade.plan]}</strong> on{' '}
                          {formatDate(scheduledDowngrade.scheduledDate)}. You'll keep your current plan until then.
                        </p>
                        <Button variant="ghost" size="sm" onClick={handleCancelScheduledDowngrade} className="mt-3">
                          Cancel Downgrade
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Current Plan Card */}
                {subscription?.expired ? (
                  <div className={`${CARD_BASE} p-6`}>
                    <div className="flex items-start gap-3 mb-4">
                      <span className={SECTION_HEADER_CHIP}>
                        <Wallet size={16} strokeWidth={1.75} />
                      </span>
                      <div>
                        <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 leading-tight">Previous Plan (Expired)</h2>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Upgrade to restore session creation</p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-50 capitalize">{subscription.plan?.replace('_', ' ')} Plan</h3>
                          <Badge variant="error">Expired</Badge>
                        </div>
                        <p className="text-sm text-neutral-700 dark:text-neutral-300 mb-3">
                          Your previous plan has expired.{' '}
                          {subscription.currentPeriodEnd && (
                            <>
                              Your <span className="capitalize">{subscription.plan?.replace('_', ' ')}</span> plan ended on{' '}
                              {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}.{' '}
                            </>
                          )}
                          Upgrade now to restore session creation, trainer management, and live activities.
                        </p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                          Existing data is preserved — your sessions, trainers, and reports remain intact.
                        </p>
                      </div>
                      {/* Two ways back, deliberately: renewing what you had is the
                          common case and should not require walking the plan grid to
                          find the tier you were already on. "Change plan" stays for
                          anyone who wants something different — including a cheaper
                          tier, which the backend now allows while expired. */}
                      <div className="flex flex-col sm:flex-row gap-2 self-start sm:self-auto flex-shrink-0">
                        {/* Gate on planId (raw enum), not plan (display-cased). */}
                        {subscription.planId && subscription.planId !== 'free' && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={handleRenewExpiredPlan}
                            disabled={checkoutLoading}
                          >
                            {checkoutLoading
                              ? 'Starting checkout…'
                              : `Renew ${subscription.plan?.replace('_', ' ')}`}
                          </Button>
                        )}
                        <Button
                          variant={subscription.planId && subscription.planId !== 'free' ? 'secondary' : 'primary'}
                          size="sm"
                          onClick={() => setMainTab('plans')}
                          disabled={checkoutLoading}
                        >
                          Change plan
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={`${CARD_BASE} p-6`}>
                    <div className="flex items-start gap-3 mb-4">
                      <span className={SECTION_HEADER_CHIP}>
                        <Wallet size={16} strokeWidth={1.75} />
                      </span>
                      <div>
                        <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 leading-tight">Current Plan</h2>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Your active subscription</p>
                      </div>
                    </div>
                    {subscription ? (
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-50">{subscription.plan} Plan</h3>
                            <Badge variant={subscription.status === 'active' ? 'success' : 'warning'}>{subscription.status}</Badge>
                          </div>
                          {subscription.price > 0 ? (
                            <div className="mb-4">
                              <p className="text-lg font-semibold tabular-nums text-neutral-900 dark:text-neutral-50">
                                ₹{subscription.totalWithGst?.toLocaleString('en-IN')}
                                <span className="text-sm font-normal text-neutral-500 dark:text-neutral-400">/{subscription.interval}</span>
                              </p>
                              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                                ₹{subscription.price?.toLocaleString('en-IN')} + ₹{subscription.gstAmount?.toLocaleString('en-IN')} GST ({subscription.gstRate}%)
                              </p>
                            </div>
                            ) : subscription.planId === "free" ? (
                              <p className="text-lg text-neutral-600 dark:text-neutral-400 mb-4">Free</p>
                            ) : (
                              /* A paid tier at zero cost — an admin comp, not the Free plan.
                                 Saying "Free" here contradicts the "Pro Plan" heading above it. */
                              <p className="text-lg text-neutral-600 dark:text-neutral-400 mb-4">
                                ₹0 <span className="text-sm">— granted by admin</span>
                              </p>
                            )}

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 sm:gap-y-3 text-sm">
                            <div className="flex items-center gap-2 sm:block">
                              <span className="text-neutral-500 dark:text-neutral-400 min-w-[90px] sm:min-w-0">Sessions:</span>
                              <span className="sm:ml-2 font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
                                {subscription.features.sessions === -1 ? 'Unlimited' : `${subscription.features.sessions}/month`}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 sm:block">
                              <span className="text-neutral-500 dark:text-neutral-400 min-w-[90px] sm:min-w-0">Participants:</span>
                              <span className="sm:ml-2 font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
                                {subscription.features.participants === -1 ? 'Unlimited' : `${subscription.features.participants}/session`}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 sm:block">
                              <span className="text-neutral-500 dark:text-neutral-400 min-w-[90px] sm:min-w-0">Trainers:</span>
                              <span className="sm:ml-2 font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
                                {subscription.features.trainers === -1 ? 'Unlimited' : subscription.features.trainers}
                              </span>
                            </div>
                          </div>
                        </div>

                        <Button variant="primary" size="sm" onClick={() => setMainTab('plans')} className="self-start sm:self-auto flex-shrink-0">
                          Change Plan
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <span className="inline-flex w-14 h-14 rounded-2xl items-center justify-center bg-primary-50 text-primary-500 ring-1 ring-inset ring-primary-100 dark:bg-primary-500/10 dark:text-primary-400 dark:ring-primary-500/20 mb-4">
                          <Wallet size={22} strokeWidth={1.75} />
                        </span>
                        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 mb-2">Free Plan</h3>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">Upgrade to unlock more features!</p>
                        <Button variant="primary" onClick={() => setMainTab('plans')}>
                          View Plans &amp; Upgrade
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Usage Statistics */}
                {usage && (
                  <div className={`${CARD_BASE} p-6`}>
                    <div className="flex items-start gap-3 mb-4">
                      <span className={SECTION_HEADER_CHIP}>
                        <CalendarRange size={16} strokeWidth={1.75} />
                      </span>
                      <div>
                        <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 leading-tight">Usage Statistics</h2>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Current period usage</p>
                      </div>
                    </div>
                    <div className="space-y-6">
                      <UsageMeter label="Total Sessions Created" used={usage.sessions.used} limit={usage.sessions.limit} showUpgrade onUpgrade={() => setMainTab('plans')} />
                      <UsageMeter label="Trainers" used={usage.trainers.used} limit={usage.trainers.limit} showUpgrade onUpgrade={() => setMainTab('plans')} showAddOn={subscription?.plan !== 'free' && subscription?.plan !== 'enterprise_scale'} onAddOn={handleTrainerAddon} addOnLoading={addonLoading} />
                    </div>
                  </div>
                )}

                {/* Billing Details */}
                {subscription && (
                  <div className={`${CARD_BASE} p-6`}>
                    <div className="flex items-start gap-3 mb-4">
                      <span className={SECTION_HEADER_CHIP}>
                        <FileText size={16} strokeWidth={1.75} />
                      </span>
                      <div>
                        <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 leading-tight">Billing Details</h2>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Current period &amp; renewal</p>
                      </div>
                    </div>
                    <div className="space-y-3 text-sm">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-0.5">
                        <span className="text-neutral-500 dark:text-neutral-400">Current Period</span>
                        <span className="font-medium tabular-nums text-neutral-900 dark:text-neutral-100">
                          {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-neutral-500 dark:text-neutral-400">Next Billing Date</span>
                        <span className="font-medium tabular-nums text-neutral-900 dark:text-neutral-100">{formatDate(subscription.currentPeriodEnd)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-neutral-500 dark:text-neutral-400">Next Payment Amount</span>
                        <span className="font-medium tabular-nums text-neutral-900 dark:text-neutral-100">
                          {subscription.cancelAtPeriodEnd ? '₹0.00 (Cancelled)' : `₹${subscription.totalWithGst?.toLocaleString('en-IN')}`}
                        </span>
                      </div>
                      {!subscription.cancelAtPeriodEnd && subscription.price > 0 && (
                        <div className="flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400">
                          <span>Breakdown</span>
                          <span className="tabular-nums">₹{subscription.price?.toLocaleString('en-IN')} + ₹{subscription.gstAmount?.toLocaleString('en-IN')} GST ({subscription.gstRate}%)</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-neutral-500 dark:text-neutral-400">Auto-Renew</span>
                        <Badge variant={subscription.autoRenew ? 'success' : 'warning'}>
                          {subscription.autoRenew ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                )}

                {/* Subscription Actions */}
                {subscription && !subscription.cancelAtPeriodEnd && (
                  <div className={`${CARD_BASE} p-6`}>
                    <div className="flex items-start gap-3 mb-4">
                      <span className={SECTION_HEADER_CHIP}>
                        <AlertTriangle size={16} strokeWidth={1.75} />
                      </span>
                      <div>
                        <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 leading-tight">Subscription Actions</h2>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Manage cancellation</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="rounded-xl bg-neutral-50/70 dark:bg-neutral-900/60 ring-1 ring-inset ring-neutral-100 dark:ring-neutral-800 p-4">
                        <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">
                          Your subscription renews automatically on <strong>{formatDate(subscription.currentPeriodEnd)}</strong>. The payment will be charged to your saved payment method. If you don't want to be charged for the next billing cycle, you can cancel your subscription below. You'll continue to have full access until the end of your current billing period.
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div>
                          <h4 className="text-sm font-semibold text-rose-700 dark:text-rose-400 mb-1">Cancel Subscription</h4>
                          <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400">Stops auto-renewal. You'll have access until {formatDate(subscription.currentPeriodEnd)}.</p>
                        </div>
                        <Button variant="danger" size="sm" onClick={() => setShowCancelModal(true)} className="self-start sm:self-auto flex-shrink-0">
                          Cancel Plan
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Payment Methods Sub Tab */}
            {subTab === 'payment-methods' && (
              <div className={`${CARD_BASE} p-6`}>
                <div className="flex items-start gap-3 mb-4">
                  <span className={SECTION_HEADER_CHIP}>
                    <Lock size={16} strokeWidth={1.75} />
                  </span>
                  <div>
                    <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 leading-tight">Payment Methods</h2>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Cards on file</p>
                  </div>
                </div>
                {paymentMethods.length > 0 ? (
                  <div className="space-y-3">
                    {paymentMethods.map((pm) => (
                      <PaymentMethodCard
                        key={pm.id}
                        paymentMethod={pm}
                        isDefault={pm.isDefault}
                        onSetDefault={handleSetDefaultPayment}
                        onDelete={handleDeletePayment}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="rounded-xl bg-blue-50 dark:bg-blue-500/10 ring-1 ring-inset ring-blue-200/60 dark:ring-blue-500/20 p-4">
                      <div className="flex items-start gap-3">
                        <Lock size={20} strokeWidth={1.75} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">Secure Payment Processing</p>
                          <p className="text-sm text-blue-800/90 dark:text-blue-300/90">
                            Payment methods are securely managed through our payment partner, Dodo Payments. When you subscribe or upgrade your plan, you'll be redirected to a secure checkout page to enter your payment details.
                          </p>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      Your payment information is never stored on our servers. To add or update a payment method, choose a plan and complete the secure checkout process.
                    </p>
                    <Button variant="primary" onClick={() => setMainTab('plans')}>View Plans</Button>
                  </div>
                )}
              </div>
            )}

            {/* Invoices Sub Tab */}
            {subTab === 'invoices' && (
              <div className={`${CARD_BASE} p-6`}>
                <div className="flex items-start gap-3 mb-4">
                  <span className={SECTION_HEADER_CHIP}>
                    <FileText size={16} strokeWidth={1.75} />
                  </span>
                  <div>
                    <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 leading-tight">Billing History</h2>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">All past invoices</p>
                  </div>
                </div>
                <InvoiceList invoices={invoices} />
              </div>
            )}
          </div>
        )}

        {/* All Plans Tab */}
        {mainTab === 'plans' && (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50 mb-2">Choose the Right Plan for You</h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">Select a plan that fits your training needs. Upgrade or downgrade anytime.</p>

              {/* Plan Type Toggle */}
              <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800">
                {[
                  { value: 'monthly', label: 'Monthly Plans' },
                  { value: 'annual',  label: 'Enterprise Plans' },
                ].map((o) => {
                  const active = billingCycle === o.value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => setBillingCycle(o.value)}
                      className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        active
                          ? 'bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-200 dark:bg-primary-500/10 dark:text-primary-300 dark:ring-primary-500/30 shadow-sm'
                          : 'text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100'
                      }`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Monthly Plans — Free always shown; if the org already moved past
                Free, the card is dimmed and the button is disabled with an
                "Already used" label. handleSelectPlan also blocks downgrade
                attempts in case the button is clicked anyway. */}
            {billingCycle === 'monthly' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
                {plans.map((plan, index) => {
                    const priceInfo = getPlanPrice(plan);
                    /**
                     * An EXPIRED plan is not a current plan.
                     *
                     * subscriptionCron leaves organization.plan on the last paid tier
                     * when it lapses, so planId still reads 'pro' after Pro expired.
                     * Without the `!expired` guard that tier renders as "CURRENT PLAN"
                     * with a disabled button — telling someone who has lost access that
                     * they already have it, and giving them no way to buy it back. The
                     * server stopped rejecting this purchase; the grid has to offer it.
                     */
                    const isCurrentPlan = subscription?.planId === plan.id && !subscription?.expired;
                    const freeAlreadyUsed =
                      plan.id === 'free' &&
                      !!subscription?.planId &&
                      subscription.planId !== 'free';
                    return (
                      <motion.div
                        key={plan.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={`relative rounded-2xl bg-white dark:bg-neutral-900 p-6 flex flex-col transition-all ${
                          isCurrentPlan
                            ? 'ring-2 ring-primary-400 dark:ring-primary-500/50 shadow-[0_4px_16px_-4px_rgba(255,159,67,0.25)]'
                            : 'ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.08)] hover:ring-neutral-300 dark:hover:ring-neutral-700'
                        } ${freeAlreadyUsed ? 'opacity-60' : ''}`}
                      >
                        {isCurrentPlan && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                            <span className="bg-gradient-to-r from-amber-500 to-primary-500 text-white text-[11px] font-semibold uppercase tracking-[0.06em] px-3 py-1 rounded-full shadow-sm">
                              Current Plan
                            </span>
                          </div>
                        )}
                        {freeAlreadyUsed && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                            <span className="bg-neutral-700 text-white text-[11px] font-semibold uppercase tracking-[0.06em] px-3 py-1 rounded-full shadow-sm">
                              Already Used
                            </span>
                          </div>
                        )}

                        <div className="flex-1">
                          <div className="text-center mb-6">
                            <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-50 mb-2">{plan.name}</h3>
                            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4 leading-relaxed">{plan.description}</p>

                            <div className="mb-2">
                              <div className="flex items-baseline justify-center gap-1 flex-wrap">
                                <span className="text-3xl font-semibold tabular-nums tracking-tight text-neutral-900 dark:text-neutral-50">
                                  ₹{priceInfo.price.toLocaleString('en-IN')}
                                </span>
                                <span className="text-sm text-neutral-500 dark:text-neutral-400">/{priceInfo.interval}</span>
                                {priceInfo.price > 0 && (
                                  <span className="text-xs text-neutral-400">+GST</span>
                                )}
                              </div>
                              {priceInfo.priceUSD > 0 && (
                                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1 tabular-nums">
                                  ${priceInfo.priceUSD.toLocaleString('en-US')}/{priceInfo.interval}
                                </p>
                              )}
                              {priceInfo.savings > 0 && (
                                <div className="mt-2">
                                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20">
                                    <CheckCircle2 size={12} strokeWidth={2} />
                                    Save ₹{priceInfo.savings.toLocaleString('en-IN')} | ${priceInfo.savingsUSD}/year
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          <ul className="space-y-3 mb-6">
                            {plan.features.map((feature, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                                <span className="w-5 h-5 rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-inset ring-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <Check size={12} strokeWidth={2.5} />
                                </span>
                                <span className="leading-relaxed">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <button
                          type="button"
                          className={`w-full py-2.5 px-4 rounded-xl font-semibold text-sm transition-all mt-auto ${
                            isCurrentPlan
                              ? 'bg-gradient-to-r from-amber-500 to-primary-500 text-white cursor-default'
                              : freeAlreadyUsed
                                ? 'bg-neutral-200 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 cursor-not-allowed'
                                : 'bg-neutral-900 hover:bg-neutral-800 text-white dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-neutral-200'
                          }`}
                          onClick={() => handleSelectPlan(plan.id)}
                          disabled={isCurrentPlan || checkoutLoading || freeAlreadyUsed}
                        >
                          {checkoutLoading
                            ? 'Redirecting…'
                            : isCurrentPlan
                              ? 'Current Plan'
                              : freeAlreadyUsed
                                ? 'Already Used'
                                // The tier they just lost is a renewal, not a new
                                // purchase — "Go Pro" reads wrong to someone who was
                                // already on Pro last month.
                                : (subscription?.expired && subscription?.planId === plan.id)
                                  ? 'Renew Plan'
                                  : plan.buttonLabel}
                        </button>
                      </motion.div>
                    );
                  })}
              </div>
            )}

            {/* Enterprise Plans Section */}
            {billingCycle === 'annual' && (
              <div>
                <div className="text-center mb-8">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em] bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900 mb-3">
                    Annual Plans
                  </span>
                  <h3 className="text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">For L&amp;D Teams &amp; Training Companies</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
                  {enterprisePlans.map((plan, index) => (
                    <motion.div
                      key={plan.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + index * 0.1 }}
                      className="relative rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 hover:ring-neutral-300 dark:hover:ring-neutral-700 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.08)] p-6 transition-all flex flex-col"
                    >
                      <div className="flex-1">
                        <div className="text-center mb-6">
                          <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-50 mb-2">{plan.name}</h3>
                          <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4 leading-relaxed">{plan.description}</p>

                          <div className="mb-2">
                            {plan.isCustom ? (
                              <div className="flex items-baseline justify-center gap-1">
                                <span className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">Custom Pricing</span>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-baseline justify-center gap-1 flex-wrap">
                                  <span className="text-3xl font-semibold tabular-nums tracking-tight text-neutral-900 dark:text-neutral-50">
                                    ₹{plan.price.toLocaleString('en-IN')}
                                  </span>
                                  <span className="text-sm text-neutral-500 dark:text-neutral-400">/year</span>
                                  <span className="text-xs text-neutral-400">+GST</span>
                                </div>
                                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1 tabular-nums">
                                  ${plan.priceUSD.toLocaleString('en-US')}/year
                                </p>
                              </>
                            )}
                          </div>
                        </div>

                        <ul className="space-y-3 mb-6">
                          {plan.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                              <span className="w-5 h-5 rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-inset ring-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Check size={12} strokeWidth={2.5} />
                              </span>
                              <span className="leading-relaxed">{feature}</span>
                            </li>
                          ))}
                        </ul>

                        {/* Add-ons Section for Enterprise Scale */}
                        {plan.addOns && plan.addOns.length > 0 && (
                          <div className="mb-6 pt-4 border-t border-neutral-100 dark:border-neutral-800/70">
                            <h4 className="text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-[0.06em] mb-3">Available Add-ons</h4>
                            <ul className="space-y-2">
                              {plan.addOns.map((addOn, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-xs text-neutral-600 dark:text-neutral-400">
                                  <span className="w-4 h-4 rounded-full bg-primary-50 text-primary-600 ring-1 ring-inset ring-primary-100 dark:bg-primary-500/10 dark:text-primary-400 dark:ring-primary-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <Plus size={10} strokeWidth={2.5} />
                                  </span>
                                  <div>
                                    <span className="font-medium text-neutral-700 dark:text-neutral-300">{addOn.label}:</span>{' '}
                                    <span className="text-neutral-500 dark:text-neutral-400">{addOn.price}</span>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        className="w-full py-2.5 px-4 rounded-xl font-semibold text-sm transition-all bg-neutral-900 hover:bg-neutral-800 text-white dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-neutral-200 disabled:opacity-50 mt-auto"
                        onClick={() => handleSelectPlan(plan.id)}
                        disabled={checkoutLoading}
                      >
                        {checkoutLoading ? 'Redirecting…' : plan.buttonLabel}
                      </button>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* FAQ Section */}
            <div>
              <div className="text-center mb-6 sm:mb-8">
                <h2 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50 mb-2">Frequently Asked Questions</h2>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Everything you need to know about Aktivity pricing and subscriptions</p>
              </div>

              <div className="max-w-3xl mx-auto space-y-3">
                {[
                  {
                    question: 'Is Aktivity free to use?',
                    answer: 'Aktivity offers a one-time Free Trial with 3 sessions and up to 30 participants per session. After the trial, you’ll need to choose a plan to continue using the platform. This ensures teams use Aktivity in real training environments, not demos.',
                  },
                  {
                    question: 'What happens after my Free Trial ends?',
                    answer: 'Once the trial is completed, you’ll be prompted to select a plan to continue running sessions and accessing reports. Your data remains safe and available when you upgrade.',
                  },
                  {
                    question: 'Do all plans include the same features?',
                    answer: 'Yes. Every plan includes all core learning features, including reflections, challenges, action plans, feedback, and reports. Pricing only changes based on: 1. Number of sessions 2. Participant volume 3. Number of trainer accounts',
                  },
                  {
                    question: 'What does "Trainer Access" mean?',
                    answer: 'Trainer access refers to who can create, manage, and run sessions. 1. Pro → 1 trainer, 2.Pro Plus → Up to 3 trainers, 3.Enterprise → Custom team size. This keeps pricing fair for solo trainers while supporting teams at scale.',
                  },
                  {
                    question: 'Can I add more trainers to my plan?',
                    answer: 'Yes. You can add additional trainer accounts as an add-on at any time. 1.₹1,000 / trainer / month (India) 2.$20 / trainer / month (Global)',
                  },
                  {
                    question: 'Is Aktivity an LMS?',
                    answer: 'No. Aktivity does not replace your LMS. It complements your LMS by capturing what LMS platforms don’t: 1.Learner challenges, 2.Reflection notes, 3.Action plans, 4.Topic-level learning intelligence',
                  },
                  {
                    question: 'Can Aktivity be used for in-person training?',
                    answer: 'Absolutely. Aktivity is designed for: 1.Instructor-led training (ILT), 2.Virtual sessions (VILT), 3.Hybrid programs. Even when cameras are off.',
                  },
                  {
                    question: 'How is Aktivity different from tools like Slido or Mentimeter?',
                    answer: 'Engagement tools focus on interaction during sessions. Aktivity focuses on learning intelligence after the interaction. We don’t just ask questions — we capture: 1.What learners struggled with, 2.What they committed to apply, 3.How confident they feel to act',
                  },
                  {
                    question: 'Do you offer discounts for annual plans?',
                    answer: 'Yes. Annual plans are available and offer better value than monthly billing. For Enterprise plans, pricing is customized based on scale, region, and usage.',
                  },
                  {
                    question: 'Why isn\'t Enterprise pricing listed?',
                    answer: 'Enterprise needs vary significantly by: 1.Number of trainers, 2.Participant volume, 3.Regions, 4.Reporting depth. To keep pricing fair and flexible, Enterprise Scale plans are custom-priced. Our team will help you choose the right setup.',
                  },
                  {
                    question: 'Is my data secure?',
                    answer: 'Yes. All data is securely stored and accessible only to authorized users within your account. We follow standard security and access best practices.',
                  },
                  {
                    question: 'Can I upgrade or downgrade my plan?',
                    answer: 'Yes, You can upgrade, downgrade, or switch plans as your training needs evolve.',
                  },
                ].map((faq, index) => (
                  <details
                    key={index}
                    className={`group ${CARD_BASE} overflow-hidden`}
                  >
                    <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-neutral-50/70 dark:hover:bg-neutral-900/60 transition-colors gap-4">
                      <span className="font-medium text-neutral-900 dark:text-neutral-100 text-sm sm:text-base">{faq.question}</span>
                      <ChevronDown size={18} strokeWidth={1.75} className="text-neutral-400 flex-shrink-0 transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="px-5 pb-5 text-sm text-neutral-600 dark:text-neutral-400 border-t border-neutral-100 dark:border-neutral-800/70 pt-4 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>

              {/* Contact Support */}
              <div className="text-center mt-8 pt-8 border-t border-neutral-200 dark:border-neutral-800/70">
                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-1">Still have questions?</p>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">Talk to us — we'll help you choose the right plan for your training scale.</p>
                <a
                  href="mailto:support@aktivity.io"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
                >
                  <Mail size={16} strokeWidth={1.75} />
                  Contact Support
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cancel Subscription Modal */}
      <Modal isOpen={showCancelModal} onClose={() => setShowCancelModal(false)} title="Cancel Subscription" size="md">
        <div>
          <div className="mb-6">
            <p className="text-base font-semibold text-neutral-900 dark:text-neutral-50 mb-2">We're sorry to see you go!</p>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4 leading-relaxed">
              Your subscription will remain active until {subscription && formatDate(subscription.currentPeriodEnd)}. After that, you'll be downgraded to the Free plan.
            </p>

            <div className="rounded-xl bg-neutral-50 dark:bg-neutral-900 ring-1 ring-inset ring-neutral-100 dark:ring-neutral-800 p-4 mb-4">
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-2">You'll lose access to:</p>
              <ul className="text-sm text-neutral-700 dark:text-neutral-300 space-y-1">
                {(() => {
                  const currentPlan = [...plans, ...enterprisePlans].find(p => p.id === subscription?.plan);
                  return currentPlan?.features?.map((feature, idx) => (
                    <li key={idx}>• {feature}</li>
                  )) || <li>• Your current plan features</li>;
                })()}
              </ul>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">You'll be moved to the Free Trial plan (3 sessions total).</p>
            </div>

            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Tell us why you're cancelling (optional)
            </label>
            <textarea
              className="w-full rounded-xl bg-neutral-50 dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 placeholder:text-neutral-400 text-neutral-900 dark:text-neutral-100"
              rows="3"
              placeholder="Your feedback helps us improve…"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
          </div>

          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => setShowCancelModal(false)} fullWidth>
              Keep Subscription
            </Button>
            <Button variant="danger" onClick={handleCancelSubscription} fullWidth>
              Confirm Cancellation
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add Payment Method Modal — Informational (PCI compliant) */}
      <Modal isOpen={showAddPaymentModal} onClose={() => setShowAddPaymentModal(false)} title="Payment Methods" size="md">
        <div className="space-y-4">
          <div className="rounded-xl bg-blue-50 dark:bg-blue-500/10 ring-1 ring-inset ring-blue-200/60 dark:ring-blue-500/20 p-4">
            <div className="flex items-start gap-3">
              <Lock size={20} strokeWidth={1.75} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">Secure Payment Processing</p>
                <p className="text-sm text-blue-800/90 dark:text-blue-300/90 leading-relaxed">
                  Payment methods are securely managed through our payment partner, Dodo Payments. When you upgrade your plan, you'll be redirected to a secure checkout page to enter your payment details.
                </p>
              </div>
            </div>
          </div>

          <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
            Your payment information is never stored on our servers. To add or update a payment method, simply choose a plan and complete the secure checkout process.
          </p>

          <div className="flex gap-3 pt-2">
            <Button variant="ghost" onClick={() => setShowAddPaymentModal(false)} fullWidth>
              Close
            </Button>
            <Button variant="primary" onClick={() => { setShowAddPaymentModal(false); setMainTab('plans'); }} fullWidth>
              View Plans
            </Button>
          </div>
        </div>
      </Modal>

      {/* Upgrade Confirmation Modal */}
      <Modal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} title="Confirm Upgrade" size="md">
        <div>
          <div className="mb-6">
            <p className="text-base font-semibold text-neutral-900 dark:text-neutral-50 mb-2">
              Upgrade to {PLAN_NAMES[selectedPlanForAction]}
            </p>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4 leading-relaxed">
              You'll be redirected to a secure checkout to complete your upgrade. Your current subscription will be cancelled and you'll be charged for the new plan immediately.
            </p>

            <div className="rounded-xl bg-amber-50 dark:bg-amber-500/10 ring-1 ring-inset ring-amber-200/60 dark:ring-amber-500/20 p-4 mb-4">
              <p className="text-sm text-amber-900 dark:text-amber-200 leading-relaxed">
                <strong>Note:</strong> Your remaining days on the current plan are not prorated. The new plan starts immediately after payment.
              </p>
            </div>

            {subscription && (
              <div className="rounded-xl bg-neutral-50 dark:bg-neutral-900 ring-1 ring-inset ring-neutral-100 dark:ring-neutral-800 p-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-neutral-500 dark:text-neutral-400">Current Plan</span>
                  <span className="font-medium text-neutral-900 dark:text-neutral-100">{subscription.plan}</span>
                </div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-neutral-500 dark:text-neutral-400">New Plan</span>
                  <span className="font-medium text-primary-600 dark:text-primary-400">{PLAN_NAMES[selectedPlanForAction]}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-neutral-500 dark:text-neutral-400">Current Period Ends</span>
                  <span className="font-medium tabular-nums text-neutral-900 dark:text-neutral-100">{formatDate(subscription.currentPeriodEnd)}</span>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => setShowUpgradeModal(false)} fullWidth>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirmUpgrade} disabled={checkoutLoading} fullWidth>
              {checkoutLoading ? (
                <>
                  <Loader2 size={14} strokeWidth={1.75} className="animate-spin mr-1.5" />
                  Redirecting…
                </>
              ) : 'Proceed to Checkout'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Downgrade Confirmation Modal */}
      <Modal isOpen={showDowngradeModal} onClose={() => setShowDowngradeModal(false)} title="Confirm Downgrade" size="md">
        <div>
          <div className="mb-6">
            <p className="text-base font-semibold text-neutral-900 dark:text-neutral-50 mb-2">
              Downgrade to {PLAN_NAMES[selectedPlanForAction]}
            </p>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4 leading-relaxed">
              Your plan will change to {PLAN_NAMES[selectedPlanForAction]} at the end of your current billing period.
              You'll keep full access to your current plan until then.
            </p>

            {subscription && (
              <div className="rounded-xl bg-neutral-50 dark:bg-neutral-900 ring-1 ring-inset ring-neutral-100 dark:ring-neutral-800 p-4 mb-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-neutral-500 dark:text-neutral-400">Current Plan</span>
                  <span className="font-medium text-neutral-900 dark:text-neutral-100">{subscription.plan}</span>
                </div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-neutral-500 dark:text-neutral-400">New Plan (after period ends)</span>
                  <span className="font-medium text-amber-600 dark:text-amber-400">{PLAN_NAMES[selectedPlanForAction]}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-neutral-500 dark:text-neutral-400">Change Takes Effect</span>
                  <span className="font-medium tabular-nums text-neutral-900 dark:text-neutral-100">{formatDate(subscription.currentPeriodEnd)}</span>
                </div>
              </div>
            )}

            <div className="rounded-xl bg-amber-50 dark:bg-amber-500/10 ring-1 ring-inset ring-amber-200/60 dark:ring-amber-500/20 p-4">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-2">Your new plan will include:</p>
              <ul className="text-sm text-amber-800/90 dark:text-amber-300/90 space-y-1">
                {(() => {
                  const targetPlan = [...plans, ...enterprisePlans].find(p => p.id === selectedPlanForAction);
                  return targetPlan?.features?.map((feature, idx) => (
                    <li key={idx}>- {feature}</li>
                  )) || <li>- Selected plan features</li>;
                })()}
              </ul>
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => setShowDowngradeModal(false)} fullWidth>
              Keep Current Plan
            </Button>
            <Button variant="danger" onClick={handleConfirmDowngrade} disabled={downgradeLoading} fullWidth>
              {downgradeLoading ? (
                <>
                  <Loader2 size={14} strokeWidth={1.75} className="animate-spin mr-1.5" />
                  Scheduling…
                </>
              ) : 'Confirm Downgrade'}
            </Button>
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default SubscriptionPage;
